import React, { useEffect, useState } from 'react';
import { arrayOf, bool, func, number, objectOf, shape, string } from 'prop-types';
import { eventNames, reportToSegment, types } from '@smartcar/morse';
import { Button, Divider, Typography, useTheme } from '@mui/material';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';

import staticText from '../../../../../../localization/Application/webhooks';
import useStepper from '../WebhooksForm/useStepper';
import { Stepper } from '../../../../../../components/Form/components';
import { WebhookDetails, WebhookReview, WebhookSignals, WebhookTemplates, WebhookVerify } from './components';
import { WebhookFormContainer, WebhookFormFooter, WebhookFormHeader, WebhookStepContainer } from './styles';
import useWebhookFormState from './useWebhookFormState';
import { formatWebhookForm, getSelectedSignalCount } from './util';
import api from '../../../../../../services/api/api';

const WebhookForm = ({
  actions: {
    createWebhookRequest,
    updateWebhookRequest,
    clearFormErrors,
    reportError,
  },
  mode,
  webhook,
  signals,
  toggleWebhookModal,
  toggleWebhookExitModal,
  setDisableExitModal,
  savingWebhookStatus,
  verifyingWebhookStatus,
  errorFetchingSignals,
  applicationId,
  webhookVerificationError,
}) => {
  const theme = useTheme();
  const addModalText = staticText.addModal;

  const {
    webhookFormValues,
    handleSignalClick,
    updateWebhookFormValues,
    handleInputBlur,
    erroredFields,
    selectedTemplate,
    setSelectedTemplate,
    nextEnabled,
    setNextEnabled,
    hasEditedWebhook,
  } = useWebhookFormState(signals, webhook);

  const editingExistingWebhook = mode === 'edit';
  const [webhookSubmitted, setWebhookSubmitted] = useState(false);
  const [verifyAttempted, setVerifyAttempted] = useState(false);
  const [liveVehicleCount, setLiveVehicleCount] = useState(undefined);

  const requiresReverification = editingExistingWebhook && (
    webhook.callbackUri !== webhookFormValues.callbackUri ||
    // reverifying not required if removing errorCallbackUri
    (webhookFormValues.errorCallbackUri &&
      webhook.errorCallbackUri !== webhookFormValues.errorCallbackUri)
  );

  const stepperSections = addModalText.stepperSections.map(section => section.title);

  const {
    activeStep,
    isLastStep,
    handleBack,
    handleNext,
    handleStep,
    // swap to defaultStep 0 when we introduce templates
    // (skip template step if editing existing webhook)
  } = useStepper({ sections: stepperSections, defaultStep: 1 });

  const handleStepChange = (direction) => {
    if (direction === 'next') {
      handleNext();
    } else {
      handleBack();
    }
  };

  const closeModal = () => {
    toggleWebhookModal({ nextModal: '', curModal: '' });
  };

  const handleExitClick = () => {
    if (activeStep === 0) {
      closeModal();
    } else {
      toggleWebhookExitModal();
    }
  };

  const createOrUpdateWebhook = (challengeToVerify = null) => {
    const formattedValues = formatWebhookForm(webhookFormValues);

    if (editingExistingWebhook) {
      const enablingAutoSubscribe = webhookFormValues.autoSubscribe === 'all' && webhook.autoSubscribe === 'none';
      updateWebhookRequest(
        webhook.id,
        formattedValues,
        challengeToVerify,
        enablingAutoSubscribe ? liveVehicleCount : null,
      );
    } else {
      const enablingAutoSubscribe = webhookFormValues.autoSubscribe === 'all';
      createWebhookRequest(
        formattedValues,
        challengeToVerify,
        enablingAutoSubscribe ? liveVehicleCount : null,
      );
    }
    reportToSegment(types.TRACK, eventNames.formSubmitted, {
      label: `${editingExistingWebhook ? 'edit' : 'add'} webhook`,
      form_content: { values: formattedValues },
    });
  };

  const handleCreateAndVerifyClick = () => {
    const challengeToVerify = addModalText.stepperSections[4].code;
    createOrUpdateWebhook(challengeToVerify);
    setVerifyAttempted(true);
  };

  // also applies to updating existing webhooks that don't require re-verification
  const handleCreateOnlyClick = () => {
    createOrUpdateWebhook();
    setWebhookSubmitted(true);
  };

  const saveInProgress = verifyingWebhookStatus === 'verifying' || savingWebhookStatus === 'saving';

  const editCtas = requiresReverification || (webhook && ['UNVERIFIED', 'INACTIVE'].includes(webhook.status)) ? (
    <React.Fragment>
      <Button
        variant="outlined"
        color="secondary"
        onClick={handleCreateOnlyClick}
        disabled={saveInProgress}
      >{addModalText.updateOnly}
      </Button>
      <Button
        variant="outlined"
        onClick={handleCreateAndVerifyClick}
        disabled={saveInProgress}
      >{addModalText.updateAndVerify}
      </Button>
    </React.Fragment>
  ) : (
    <Button
      variant="outlined"
      onClick={handleCreateOnlyClick}
      disabled={saveInProgress}
    >{addModalText.update}
    </Button>
  );

  const addCtas = verifyingWebhookStatus === 'failure' ? (
    <Button variant="outlined" onClick={closeModal}>
      {addModalText.verifyLater}
    </Button>
  ) : (
    <React.Fragment>
      <Button
        variant="outlined"
        color="secondary"
        onClick={handleCreateOnlyClick}
        disabled={saveInProgress}
      >{editingExistingWebhook ? addModalText.updateOnly : addModalText.addOnly}
      </Button>
      <Button
        variant="outlined"
        onClick={handleCreateAndVerifyClick}
        disabled={saveInProgress}
      >{editingExistingWebhook ? addModalText.updateAndVerify : addModalText.addAndVerify}
      </Button>
    </React.Fragment>
  );

  useEffect(() => {
    // if skipping verification step,
    // only close modal after the webhook has saved successfully
    if (webhookSubmitted && savingWebhookStatus === 'success') {
      closeModal();
    }
  }, [webhookSubmitted, savingWebhookStatus]);

  useEffect(() => {
    // if saving and verifying,
    // only close modal after successful verification
    if (verifyAttempted && verifyingWebhookStatus === 'success') {
      closeModal();
    }
  }, [verifyAttempted, verifyingWebhookStatus]);

  useEffect(() => {
    // once a user has edited a new or existing webhook,
    // enable the early exit modal if they click 'close' early
    if (hasEditedWebhook) {
      setDisableExitModal(false);
    }
  }, [hasEditedWebhook]);

  useEffect(() => {
    api.fetchConnectedVehicles(applicationId, { mode: 'live' })
      .then((result) => {
        setLiveVehicleCount(result.data.paging && result.data.paging.count);
      })
      .catch((error) => {
        reportError(error);
      });
    return () => {
      clearFormErrors();
    };
  }, []);

  useEffect(() => {
    if (mode === 'add' && savingWebhookStatus === 'success') {
      setDisableExitModal(true);
    }
  }, [mode, savingWebhookStatus]);

  return (
    <WebhookFormContainer>
      <WebhookFormHeader>
        <Button
          onClick={handleExitClick}
          sx={{
            fontFamily: theme.typography.h1.fontFamily,
            fontSize: theme.typography.body1.fontSize,
            color: theme.palette.grey[600],
            paddingRight: theme.spacing(0.5),
            zIndex: theme.zIndex.modal,
            ':hover': {
              color: theme.palette.primary.main,
              textDecoration: 'none',
            },
          }}
        >{staticText.heading}
        </Button>
        <ChevronRightRoundedIcon fontSize="small" />
        <Typography
          variant="bold"
          sx={{
            fontFamily: theme.typography.h1.fontFamily,
            fontSize: theme.typography.body1.fontSize,
            paddingLeft: theme.spacing(0.5),
          }}
        >{editingExistingWebhook ? addModalText.edit : addModalText.title}
        </Typography>
      </WebhookFormHeader>
      <Divider />

      <WebhookStepContainer>
        {activeStep !== 0 && (
          <div>
            <Stepper
              steps={stepperSections}
              activeStep={activeStep}
              handleStep={handleStep}
              orientation="vertical"
              hiddenStepIndexes={[0]}
            />
          </div>
        )}

        {activeStep === 0 && (
          <WebhookTemplates
            selectedTemplate={selectedTemplate}
            setSelectedTemplate={setSelectedTemplate}
            setNextEnabled={setNextEnabled}
          />
        )}
        {activeStep === 1 && (
          <WebhookSignals
            signals={signals.signalsByGroup}
            selectedSignals={webhookFormValues.signalNames}
            handleSignalClick={handleSignalClick}
            setNextEnabled={setNextEnabled}
            updateWebhookFormValues={updateWebhookFormValues}
            totalSignalRecords={signals.totalSignalRecords}
            errorFetchingSignals={errorFetchingSignals}
          />
        )}
        {activeStep === 2 && (
          <WebhookDetails
            formValues={webhookFormValues}
            updateWebhookFormValues={updateWebhookFormValues}
            handleInputBlur={handleInputBlur}
            erroredFields={erroredFields}
            setNextEnabled={setNextEnabled}
            liveVehicleCount={liveVehicleCount}
          />
        )}
        {activeStep === 3 && (
          <WebhookReview
            signals={signals.signalsByGroup}
            formValues={webhookFormValues}
            setNextEnabled={setNextEnabled}
            selectedSignalCount={
              getSelectedSignalCount(signals.signalsByGroup, webhookFormValues.signalNames)
            }
            webhook={webhook}
            applicationId={applicationId}
            liveVehicleCount={liveVehicleCount}
          />
        )}
        {activeStep === 4 && (
          <WebhookVerify
            previouslyVerified={editingExistingWebhook && webhook.status === 'ACTIVE' && !requiresReverification}
            requiresReverification={editingExistingWebhook && webhook.status === 'ACTIVE' && requiresReverification}
            errorSavingWebhook={savingWebhookStatus === 'failure'}
            errorVerifyingWebhook={verifyingWebhookStatus === 'failure'}
            webhookVerificationError={webhookVerificationError}
          />
        )}
      </WebhookStepContainer>

      <Divider />
      <WebhookFormFooter>
        {activeStep !== 1
          && !(mode === 'add' && savingWebhookStatus === 'success')
          && (
            <Button
              variant={isLastStep ? 'text' : 'outlined'}
              color="secondary"
              onClick={() => handleStepChange('back')}
              disabled={activeStep === 0 || saveInProgress}
            >{addModalText.back}
            </Button>
          )}
        {/* eslint-disable-next-line no-nested-ternary */}
        {isLastStep ? (
          editingExistingWebhook ? editCtas : addCtas
        ) : (
          <Button
            variant="outlined"
            onClick={() => handleStepChange('next')}
            disabled={!nextEnabled}
          >{addModalText.next}
          </Button>
        )}
      </WebhookFormFooter>
    </WebhookFormContainer>
  );
};

export default WebhookForm;

WebhookForm.propTypes = {
  actions: shape({
    createWebhookRequest: func.isRequired,
    updateWebhookRequest: func.isRequired,
    clearFormErrors: func.isRequired,
    reportError: func.isRequired,
  }).isRequired,
  mode: string.isRequired,
  webhook: shape({
    id: string.isRequired,
  }),
  signals: shape({
    signalsByGroup: objectOf({
      [string]: arrayOf(shape({
        name: string.isRequired,
        groupName: string.isRequired,
        description: string.isRequired,
        permission: string.isRequired,
      })).isRequired,
    }).isRequired,
    totalSignalRecords: number.isRequired,
  }).isRequired,
  toggleWebhookModal: func.isRequired,
  toggleWebhookExitModal: func.isRequired,
  setDisableExitModal: func.isRequired,
  savingWebhookStatus: string.isRequired,
  verifyingWebhookStatus: string.isRequired,
  errorFetchingSignals: bool.isRequired,
  applicationId: string.isRequired,
  webhookVerificationError: string,
};

WebhookForm.defaultProps = {
  webhook: undefined,
  webhookVerificationError: null,
};
